import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Card,
  CardContent,
  Grid,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Delete, Refresh, Android, Edit } from '@material-ui/icons';
import { DarkTooltip } from './DarkTooltip';
import { Page, Content } from '@backstage/core-components';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import {
  Breadcrumbs,
  ErrorToast,
  InlineError,
} from '@internal/backstage-plugin-global-header';

const useStyles = makeStyles(theme => ({
  pageRoot: {
    height: '100% !important',
    '& > article': { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
    '& > article > *:first-child': { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' },
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    padding: '8px 24px 24px',
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: 2 },
  },
  statsRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
  },
  statCard: {
    width: 200,
    flexShrink: 0,
    borderRadius: 12,
    border: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative' as any,
    overflow: 'hidden',
    cursor: 'default',
    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.12)' },
  },
  statContent: {
    padding: `${theme.spacing(2)}px !important`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  statHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  statIconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statValue: { fontWeight: 700, fontSize: '2rem', lineHeight: 1 },
  statTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase' as any,
    letterSpacing: '0.8px',
    marginTop: 4,
  },
  statAccent: { position: 'absolute' as any, bottom: 0, left: 0, right: 0, height: 3 },
  importCard: {
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none !important',
    borderRadius: 8,
  },
  importCardContent: {
    padding: `${theme.spacing(2)}px !important`,
  },
  importTitle: {
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.primary,
  },
  yamlInput: {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'monospace',
      fontSize: '0.78rem',
      borderRadius: 8,
    },
  },
  importBtn: {
    height: 36,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.82rem',
    borderRadius: 8,
    backgroundColor: '#1976d2',
    color: '#fff',
    boxShadow: 'none',
    '&:hover': { backgroundColor: '#1565c0', boxShadow: 'none' },
    '&:disabled': { backgroundColor: '#90caf9', color: '#fff' },
  },
  clearBtn: {
    height: 36,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.82rem',
    borderRadius: 8,
    color: '#1976d2',
    border: '1px solid #90caf9',
    backgroundColor: '#e3f2fd',
    '&:hover': { backgroundColor: '#bbdefb' },
  },
  refreshBtn: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    '&:hover': { background: theme.palette.action.hover },
  },
  tableCard: {
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none !important',
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tableCardTitle: { fontWeight: 600, fontSize: '0.875rem' },
  tableHead: {
    '& th': {
      fontWeight: 700,
      fontSize: '0.72rem',
      textTransform: 'uppercase' as any,
      letterSpacing: '0.5px',
      color: theme.palette.text.secondary,
      backgroundColor: '#fafafa',
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
    },
  },
  tableRow: {
    '&:hover': { backgroundColor: '#f9fafb' },
    '&:last-child td': { borderBottom: 0 },
    '& td': { padding: theme.spacing(1.25, 2) },
  },
  agentName: { fontWeight: 600, fontSize: '0.875rem' },
  apiNameCode: {
    background: 'rgba(25,118,210,0.08)',
    color: '#1565c0',
    padding: '2px 8px',
    borderRadius: 6,
    fontSize: '0.75rem',
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  baseUrl: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    wordBreak: 'break-all' as any,
    maxWidth: 260,
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
    boxShadow: '0 0 0 2px rgba(0,0,0,0.08)',
  },
  activeChip: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 700,
    fontSize: '0.7rem',
    height: 22,
    '& .MuiChip-label': { paddingLeft: 8, paddingRight: 8 },
  },
  inactiveChip: {
    backgroundColor: '#fce4ec',
    color: '#c62828',
    fontWeight: 700,
    fontSize: '0.7rem',
    height: 22,
    '& .MuiChip-label': { paddingLeft: 8, paddingRight: 8 },
  },
  deleteBtn: {
    color: '#c62828',
    borderRadius: 8,
    '&:hover': { backgroundColor: '#fce4ec' },
    '&:disabled': { color: theme.palette.action.disabled },
  },
  editBtn: {
    color: '#1976d2',
    borderRadius: 8,
    '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)' },
    '&:disabled': { color: theme.palette.action.disabled },
  },
  editingBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    borderRadius: 6,
    backgroundColor: 'rgba(25,118,210,0.06)',
    border: '1px solid #90caf9',
    marginBottom: theme.spacing(1.5),
  },
  emptyState: {
    padding: theme.spacing(6),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  countChip: {
    height: 20,
    fontSize: '0.7rem',
    fontWeight: 700,
    background: 'rgba(25,118,210,0.1)',
    color: '#1976d2',
    '& .MuiChip-label': { paddingLeft: 8, paddingRight: 8 },
  },
}));

interface AgentRow {
  id: number;
  name: string;
  api_name: string;
  base_url: string;
  icon: string;
  color: string;
  is_active: boolean;
}

interface ImportResult {
  message?: string;
  error?: string;
}

export const AgentAdminPage = ({ namespace }: { namespace?: string; backPath?: string }) => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const baseUrl = configApi.getString('backend.baseUrl');

  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [agentYaml, setAgentYaml] = useState('');
  const [apiYaml, setApiYaml] = useState('');
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editingAgent, setEditingAgent] = useState<string | null>(null);
  const [fetchingYaml, setFetchingYaml] = useState<string | null>(null);
  const importCardRef = React.useRef<HTMLDivElement>(null);

  const getHeaders = async () => {
    const credentials = await identityApi.getCredentials();
    return { Authorization: `Bearer ${credentials.token}`, 'Content-Type': 'application/json' };
  };

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const headers = await getHeaders();
      const res = await fetch(
        `${baseUrl}/api/custom-api/agents/all${namespace ? `?namespace=${namespace}` : ''}`,
        { headers },
      );
      if (!res.ok) throw new Error(res.statusText);
      setAgents(await res.json());
    } catch (err) {
      setFetchError(`Failed to fetch agents: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAgents(); }, []);

  const handleImport = async () => {
    if (!agentYaml.trim() || !apiYaml.trim()) { setActionError('Both agent.yaml and api.yaml content are required.'); return; }
    try {
      setImporting(true);
      setImportResult(null);
      setActionError(null);
      const headers = await getHeaders();
      // If editing, deactivate the existing agent first so import-raw can update it
      if (editingAgent) {
        const delRes = await fetch(`${baseUrl}/api/custom-api/agents/${editingAgent}`, { method: 'DELETE', headers });
        if (!delRes.ok) throw new Error((await delRes.json()).error);
      }
      const res = await fetch(`${baseUrl}/api/custom-api/agents/import-raw`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ agentYaml, apiYaml }),
      });
      const data = await res.json();
      if (!res.ok) { setActionError(data.error || 'Import failed'); }
      else { setImportResult(data); setAgentYaml(''); setApiYaml(''); setEditingAgent(null); await fetchAgents(); }
    } catch (err) {
      setActionError(`Import failed: ${(err as Error).message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleEdit = async (agent: AgentRow) => {
    try {
      setFetchingYaml(agent.api_name);
      setActionError(null);
      const headers = await getHeaders();
      const yamlRes = await fetch(`${baseUrl}/api/custom-api/agents/${agent.api_name}/yaml`, { headers });
      if (!yamlRes.ok) throw new Error((await yamlRes.json()).error);
      const { agentYaml: ay, apiYaml: apy } = await yamlRes.json();
      setAgentYaml(ay);
      setApiYaml(apy);
      setEditingAgent(agent.api_name);
      setImportResult(null);
      importCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      setActionError(`Edit failed: ${(err as Error).message}`);
    } finally {
      setFetchingYaml(null);
    }
  };

  const handleDelete = async (apiName: string) => {
    try {
      setDeletingId(apiName);
      setActionError(null);
      const headers = await getHeaders();
      const res = await fetch(`${baseUrl}/api/custom-api/agents/${apiName}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error((await res.json()).error);
      await fetchAgents();
    } catch (err) {
      setActionError(`Delete failed: ${(err as Error).message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const activeCount = agents.filter(a => a.is_active).length;

  const stats = [
    { title: 'Total Agents', value: agents.length, color: '#1976d2' },
    { title: 'Active', value: activeCount, color: '#2e7d32' },
    { title: 'Inactive', value: agents.length - activeCount, color: '#c62828' },
  ];

  if (fetchError) {
    return (
      <Page themeId="tool" className={classes.pageRoot}>
        <Content noPadding>
          <Box className={classes.contentWrapper}>
            <Breadcrumbs />
            <Box mt={2}>
              <InlineError
                title="Agent registry could not be loaded"
                description="We were unable to fetch the registered agents. Please try again."
                onRetry={fetchAgents}
              />
            </Box>
          </Box>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool" className={classes.pageRoot}>
      <Content noPadding>
        <Box className={classes.contentWrapper}>
          <Breadcrumbs />

          {/* Stats */}
          {!loading && (
            <Box className={classes.statsRow}>
              {stats.map(s => (
                <Card key={s.title} className={classes.statCard} elevation={2}>
                  <CardContent className={classes.statContent}>
                    <Box className={classes.statHeader}>
                      <Box>
                        <Typography className={classes.statValue} style={{ color: s.color }}>{s.value}</Typography>
                        <Typography className={classes.statTitle} style={{ color: 'rgba(0,0,0,0.5)' }}>{s.title}</Typography>
                      </Box>
                      <Box className={classes.statIconBox} style={{ background: `${s.color}15` }}>
                        <Android style={{ color: s.color, fontSize: 22 }} />
                      </Box>
                    </Box>
                  </CardContent>
                  <Box className={classes.statAccent} style={{ background: s.color }} />
                </Card>
              ))}
            </Box>
          )}

          <Box className={classes.scrollArea}>
            {/* Import Section */}
            <Card className={classes.importCard} ref={importCardRef as any}>
              <CardContent className={classes.importCardContent}>
                {editingAgent && (
                  <Box className={classes.editingBanner}>
                    <Edit style={{ fontSize: 16, color: '#1976d2' }} />
                    <Typography style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1565c0' }}>
                      Editing: <span style={{ fontFamily: 'monospace' }}>{editingAgent}</span> — modify the YAMLs below and click Update
                    </Typography>
                  </Box>
                )}
                <Typography className={classes.importTitle}>{editingAgent ? 'Edit Agent YAML' : 'Import Agent from YAML'}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="agent.yaml"
                      placeholder="Paste agent.yaml content here..."
                      multiline
                      rows={10}
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={agentYaml}
                      onChange={e => setAgentYaml(e.target.value)}
                      className={classes.yamlInput}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="api.yaml"
                      placeholder="Paste api.yaml content here..."
                      multiline
                      rows={10}
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={apiYaml}
                      onChange={e => setApiYaml(e.target.value)}
                      className={classes.yamlInput}
                    />
                  </Grid>
                </Grid>
                <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 12 }}>
                  <Button
                    variant="contained"
                    className={classes.importBtn}
                    onClick={handleImport}
                    disabled={importing || !agentYaml.trim() || !apiYaml.trim()}
                    startIcon={importing ? <CircularProgress size={14} color="inherit" /> : undefined}
                  >
                    {importing ? (editingAgent ? 'Updating...' : 'Importing...') : (editingAgent ? 'Update' : 'Import')}
                  </Button>
                  {editingAgent ? (
                    <Button
                      variant="outlined"
                      className={classes.clearBtn}
                      onClick={() => { setAgentYaml(''); setApiYaml(''); setImportResult(null); setActionError(null); setEditingAgent(null); }}
                      disabled={importing}
                    >
                      Cancel
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        className={classes.clearBtn}
                        onClick={() => { setAgentYaml(''); setApiYaml(''); setImportResult(null); setActionError(null); }}
                        disabled={importing}
                      >
                        Clear
                      </Button>
                      <DarkTooltip title="Refresh agent list" arrow>
                        <IconButton size="small" className={classes.refreshBtn} onClick={fetchAgents}>
                          <Refresh fontSize="small" />
                        </IconButton>
                      </DarkTooltip>
                    </>
                  )}
                </Box>
                {importResult?.message && (
                  <Box mt={1.5}><Alert severity="success">{importResult.message}</Alert></Box>
                )}
              </CardContent>
            </Card>

            {/* Agents Table */}
            <Card className={classes.tableCard}>
              <Box className={classes.tableCardHeader}>
                <Typography className={classes.tableCardTitle}>Registered Agents</Typography>
                <Chip label={agents.length} size="small" className={classes.countChip} />
              </Box>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={5} style={{ gap: 10 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="textSecondary">Loading agents...</Typography>
                </Box>
              ) : agents.length === 0 ? (
                <Box className={classes.emptyState}>
                  <Typography style={{ fontSize: 36, opacity: 0.3 }}>🤖</Typography>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>No agents found</Typography>
                  <Typography variant="body2">Import a YAML file above to register your first agent.</Typography>
                </Box>
              ) : (
                <Table>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>API Name</TableCell>
                      <TableCell>Base URL</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {agents.map(agent => (
                      <TableRow key={agent.id} className={classes.tableRow}>
                        <TableCell>
                          <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                            <Box className={classes.colorDot} style={{ backgroundColor: agent.color }} />
                            <Typography className={classes.agentName}>{agent.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell><span className={classes.apiNameCode}>{agent.api_name}</span></TableCell>
                        <TableCell><Typography className={classes.baseUrl}>{agent.base_url}</Typography></TableCell>
                        <TableCell>
                          <Chip
                            label={agent.is_active ? '● Active' : '● Inactive'}
                            size="small"
                            className={agent.is_active ? classes.activeChip : classes.inactiveChip}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" justifyContent="center" style={{ gap: 4 }}>
                            <DarkTooltip title={agent.is_active ? 'Edit agent YAMLs' : 'Already inactive'} arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  className={classes.editBtn}
                                  onClick={() => handleEdit(agent)}
                                  disabled={!agent.is_active || fetchingYaml === agent.api_name || !!deletingId}
                                >
                                  {fetchingYaml === agent.api_name
                                    ? <CircularProgress size={14} />
                                    : <Edit fontSize="small" />}
                                </IconButton>
                              </span>
                            </DarkTooltip>
                            <DarkTooltip title={agent.is_active ? 'Deactivate agent' : 'Already inactive'} arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  className={classes.deleteBtn}
                                  onClick={() => handleDelete(agent.api_name)}
                                  disabled={!agent.is_active || deletingId === agent.api_name || !!fetchingYaml}
                                >
                                  {deletingId === agent.api_name
                                    ? <CircularProgress size={14} />
                                    : <Delete fontSize="small" />}
                                </IconButton>
                              </span>
                            </DarkTooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </Box>

          <ErrorToast message={actionError} onClose={() => setActionError(null)} />
        </Box>
      </Content>
    </Page>
  );
};
